import { useEffect } from "react";
import { useForm } from "react-hook-form";
import type { CatalogEntry, Profile, User, UserInput } from "@core/types";
import { tokens } from "@core/theme/tokens";
import {
  Field,
  FormLayout,
  inputStyle,
  PortalButton,
  useConfirmUnsavedChanges,
} from "@portal/common";
import type { UserCreateInput } from "./api";
import { userFormSchema, type UserFormValues } from "./userSchemas";
import { userStyles } from "./userStyles";

function placeName(entry: CatalogEntry) {
  return typeof entry.name === "string" ? entry.name : entry.name.es;
}

// Aviso RF-SEG-07: visible en el formulario mientras el perfil elegido sea de
// validador, no solo en el instante de enviar — "antes de guardar" se cumple
// mostrándolo mientras la condición se sostiene.
function ValidatorOnlyWarning() {
  return (
    <div
      role="note"
      style={{
        font: `500 12.5px ${tokens.font.ui}`,
        color: tokens.text.secondary,
        background: tokens.surface.surface,
        border: `1px solid ${tokens.surface.line2}`,
        borderRadius: tokens.radius.sm,
        padding: userStyles.warning.padding,
        marginTop: userStyles.warning.marginTop,
        marginBottom: userStyles.warning.marginBottom,
      }}
    >
      Este usuario{" "}
      <b style={{ color: tokens.text.primary }}>
        no podrá ingresar al portal administrativo
      </b>
      . Solo accede al validador de QR, y únicamente para el lugar asociado.
    </div>
  );
}

export function UserForm({
  entry,
  profiles,
  merchants,
  workshops,
  onSubmit,
  onCancel,
  submitting,
  modal,
}: {
  entry: User | undefined;
  profiles: Profile[];
  merchants: CatalogEntry[];
  workshops: CatalogEntry[];
  onSubmit: (input: UserCreateInput | UserInput) => void;
  onCancel: () => void;
  submitting: boolean;
  // El formulario ES el diálogo (revisión en vivo 2026-09-22 punto #4A):
  // título y acciones van a las regiones fijas del `Modal`, no al scroll.
  modal?: { label: string; width?: number | string; onClose?: () => void };
}) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors, isDirty },
  } = useForm<UserFormValues>({
    defaultValues: {
      name: entry?.name ?? "",
      username: entry?.username ?? "",
      email: entry?.email ?? "",
      phone: entry?.phone ?? "",
      profileId: entry?.profile.id ?? "",
      placeType: entry?.place?.type ?? "",
      placeId: entry?.place?.id ?? "",
    },
  });
  useConfirmUnsavedChanges(isDirty);

  // `useProfiles` trae TODOS los perfiles (el endpoint no filtra por estado):
  // se listan solo los activos como opciones nuevas (SPEC-215 §3), pero si el
  // usuario ya tenía asignado uno que hoy está inactivo, se conserva como
  // opción para no perder la selección actual sin que el operador la toque.
  const activeProfiles = profiles.filter((profile) => profile.active);
  const currentProfile = entry
    ? profiles.find((profile) => profile.id === entry.profile.id)
    : undefined;
  const profileOptions =
    currentProfile && !currentProfile.active
      ? [...activeProfiles, currentProfile]
      : activeProfiles;

  const profileId = watch("profileId");
  const placeType = watch("placeType");
  const placeId = watch("placeId");
  const selectedProfile = profiles.find((profile) => profile.id === profileId);
  const validatorOnly = Boolean(selectedProfile?.validatorOnly);

  // RF-SEG-06/3.1: al volver a un perfil de portal, el lugar se limpia acá
  // (el backend también lo fuerza, pero el operador no debería ver un lugar
  // "fantasma" en un formulario que ya no lo pide).
  useEffect(() => {
    if (!validatorOnly && (placeType || placeId)) {
      setValue("placeType", "");
      setValue("placeId", "");
    }
  }, [validatorOnly, placeType, placeId, setValue]);

  const places = placeType === "merchant" ? merchants : workshops;

  const submit = (values: UserFormValues) => {
    const parsed = userFormSchema.safeParse(values);
    if (!parsed.success) {
      for (const issue of parsed.error.issues)
        setError(issue.path[0] as keyof UserFormValues, {
          message: issue.message,
        });
      return;
    }
    const data = parsed.data;
    // PLAN-009 §3.6: "Tipo de lugar" y "Lugar asociado" son dependientes
    // (sin tipo no hay lista de lugares) — el que falta señala su propio
    // campo, cada uno con su propio mensaje.
    if (validatorOnly && !data.placeType) {
      setError("placeType", {
        message: "Selecciona un comercio aliado o un taller",
      });
      return;
    }
    if (validatorOnly && !data.placeId) {
      setError("placeId", {
        message:
          "Un perfil de validador requiere un comercio aliado o un taller asociado",
      });
      return;
    }
    const common = {
      name: data.name,
      email: data.email,
      phone: data.phone || undefined,
      profileId: data.profileId,
      merchantId:
        validatorOnly && data.placeType === "merchant" ? data.placeId : null,
      workshopId:
        validatorOnly && data.placeType === "workshop" ? data.placeId : null,
    };
    onSubmit(
      entry
        ? (common as UserInput)
        : ({ ...common, username: data.username } as UserCreateInput),
    );
  };

  // Revisión en vivo 2026-09-22, punto #21: mismo encabezado que "Nueva
  // recompensa" — la acción arriba y, como título, el nombre que se está
  // escribiendo (o el actual), con un respaldo mientras está vacío.
  const name = watch("name");

  return (
    <FormLayout
      eyebrow={entry ? "Editar usuario" : "Nuevo usuario"}
      title={name || "Usuario sin nombre"}
      {...(modal ? { modal } : {})}
      onSubmit={handleSubmit(submit)}
      actions={
        <>
          <PortalButton
            type="button"
            tone="secondary"
            size="small"
            onClick={onCancel}
          >
            Cancelar
          </PortalButton>
          <PortalButton type="submit" size="small" disabled={submitting}>
            {submitting ? "Guardando…" : "Guardar"}
          </PortalButton>
        </>
      }
    >
      <Field label="Nombre completo" required error={errors.name?.message}>
        <input {...register("name")} style={inputStyle} />
      </Field>
      {entry ? (
        <Field label="Usuario">
          <input
            value={entry.username}
            readOnly
            aria-label="Usuario no editable"
            style={{ ...inputStyle, opacity: 0.65 }}
          />
          <small style={{ color: tokens.text.tertiary }}>
            El usuario no se puede editar: es la referencia de la bitácora.
          </small>
        </Field>
      ) : (
        <Field label="Usuario" required error={errors.username?.message}>
          <input {...register("username")} style={inputStyle} />
        </Field>
      )}
      <Field label="Correo" required error={errors.email?.message}>
        <input {...register("email")} style={inputStyle} />
      </Field>
      <Field label="Teléfono">
        <input {...register("phone")} style={inputStyle} />
      </Field>
      <Field label="Perfil" required error={errors.profileId?.message}>
        <select {...register("profileId")} style={inputStyle}>
          <option value="">Selecciona un perfil</option>
          {profileOptions.map((profile) => (
            <option key={profile.id} value={profile.id}>
              {profile.name}
            </option>
          ))}
        </select>
      </Field>
      {validatorOnly ? (
        <>
          <Field
            label="Tipo de lugar"
            required
            error={errors.placeType?.message}
          >
            <select
              {...register("placeType")}
              style={inputStyle}
              onChange={(event) => {
                setValue(
                  "placeType",
                  event.target.value as UserFormValues["placeType"],
                );
                setValue("placeId", "");
              }}
            >
              <option value="">Selecciona un tipo</option>
              <option value="merchant">Comercio aliado</option>
              <option value="workshop">Taller de Teojama</option>
            </select>
          </Field>
          <Field
            label="Lugar asociado"
            required
            error={errors.placeId?.message}
          >
            <select
              {...register("placeId")}
              style={inputStyle}
              disabled={!placeType}
            >
              <option value="">
                {placeType ? "Selecciona un lugar" : "Elige primero el tipo"}
              </option>
              {places.map((place) => (
                <option key={place.id} value={place.id}>
                  {placeName(place)}
                </option>
              ))}
            </select>
          </Field>
          <ValidatorOnlyWarning />
        </>
      ) : null}
    </FormLayout>
  );
}
